import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import AdminLayout from '../../components/AdminLayout';
import StatCards from '../../components/StatCards';
import { getAllPayments, getOrderById, getProductById, IMAGE_BASE_URL } from '../../services/api';
import ExcelJS from 'exceljs';
import '../Dashboard.css';

const ITEMS_PER_PAGE = 10;

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const toDateStr = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const fmtDisp = (s) => {
  if (!s) return '';
  const d = new Date(s + 'T12:00:00');
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const buildCalendarCells = (year, month) => {
  const firstDay = new Date(year, month, 1).getDay();
  const totalDays = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) {
    cells.push(null);
  }
  for (let d = 1; d <= totalDays; d++) {
    cells.push(new Date(year, month, d));
  }
  return cells;
};

const MiniCalendar = ({
  year, month, start, end, hoverDate,
  onDayClick, onDayHover, onPrev, onNext,
  showPrev = true, showNext = true
}) => {
  const todayStr = toDateStr(new Date());
  const cells = buildCalendarCells(year, month);

  const isToday = (d) => d && toDateStr(d) === todayStr;
  const isInRange = (d) => {
    if (!d) return false;
    const s = toDateStr(d);
    const rangeEnd = end || hoverDate || '';
    if (!start || !rangeEnd) return false;
    const lo = start < rangeEnd ? start : rangeEnd;
    const hi = start < rangeEnd ? rangeEnd : start;
    return s > lo && s < hi;
  };
  const isRangeStart = (d) => {
    if (!d) return false;
    const s = toDateStr(d);
    const rangeEnd = end || hoverDate || '';
    if (!start || !rangeEnd) return s === start;
    return s === (start < rangeEnd ? start : rangeEnd);
  };
  const isRangeEnd = (d) => {
    if (!d) return false;
    const s = toDateStr(d);
    const rangeEnd = end || hoverDate || '';
    if (!start || !rangeEnd) return false;
    return s === (start < rangeEnd ? rangeEnd : start);
  };

  return (
    <div className="dc-cal">
      <div className="dc-cal-nav">
        {showPrev ? (
          <button type="button" className="dc-cal-nav-btn" onClick={onPrev}>‹</button>
        ) : (
          <div style={{ width: 28 }} />
        )}
        <span className="dc-cal-month-label">{MONTHS[month]} {year}</span>
        {showNext ? (
          <button type="button" className="dc-cal-nav-btn" onClick={onNext}>›</button>
        ) : (
          <div style={{ width: 28 }} />
        )}
      </div>
      <div className="dc-cal-weekdays">
        {WEEKDAYS.map((wd) => <div key={wd} className="dc-cal-weekday">{wd}</div>)}
      </div>
      <div className="dc-cal-days">
        {cells.map((cell, idx) => {
          if (!cell) return <div key={`b-${idx}`} className="dc-cal-day is-empty" />;
          let cls = 'dc-cal-day';
          if (isToday(cell)) cls += ' is-today';
          if (isRangeStart(cell)) cls += ' is-sel-start';
          if (isRangeEnd(cell)) cls += ' is-sel-end';
          if (isInRange(cell)) cls += ' is-in-range';
          return (
            <button
              key={toDateStr(cell)}
              type="button"
              className={cls}
              onClick={() => onDayClick && onDayClick(cell)}
              onMouseEnter={() => onDayHover && onDayHover(toDateStr(cell))}
            >
              {cell.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const DateFilterPopup = ({
  initialStart = '',
  initialEnd = '',
  initialPreset = 'today',
  onApply,
  onClose,
  hasRecords = true,
  onDownloadCSV,
}) => {
  const todayDate = new Date();
  const todayStr = toDateStr(todayDate);
  const [start, setStart] = useState(initialStart || todayStr);
  const [end, setEnd] = useState(initialEnd || todayStr);
  const [preset, setPreset] = useState(initialPreset || 'today');
  const [pickStep, setPickStep] = useState('start');
  const [hoverDate, setHoverDate] = useState('');
  const [leftYear, setLeftYear] = useState(todayDate.getFullYear());
  const [leftMonth, setLeftMonth] = useState(todayDate.getMonth() === 0 ? 11 : todayDate.getMonth() - 1);
  const rightYear = leftMonth === 11 ? leftYear + 1 : leftYear;
  const rightMonth = leftMonth === 11 ? 0 : leftMonth + 1;

  const goLeft = () => {
    if (leftMonth === 0) {
      setLeftMonth(11);
      setLeftYear((y) => y - 1);
    } else {
      setLeftMonth((m) => m - 1);
    }
  };

  const goRight = () => {
    if (leftMonth === 11) {
      setLeftMonth(0);
      setLeftYear((y) => y + 1);
    } else {
      setLeftMonth((m) => m + 1);
    }
  };

  const applyPreset = (key) => {
    setPreset(key);
    const now = new Date();
    let s = '';
    let e2 = '';
    if (key === 'today') {
      s = toDateStr(now);
      e2 = toDateStr(now);
    } else if (key === 'yesterday') {
      const y = new Date(now);
      y.setDate(now.getDate() - 1);
      s = toDateStr(y);
      e2 = toDateStr(y);
    } else if (key === '7d') {
      const d7 = new Date(now);
      d7.setDate(now.getDate() - 6);
      s = toDateStr(d7);
      e2 = toDateStr(now);
    } else if (key === '30d') {
      const d30 = new Date(now);
      d30.setDate(now.getDate() - 29);
      s = toDateStr(d30);
      e2 = toDateStr(now);
    } else if (key === 'this_month') {
      const tm = new Date(now.getFullYear(), now.getMonth(), 1);
      s = toDateStr(tm);
      e2 = toDateStr(now);
    } else if (key === 'custom') {
      setStart('');
      setEnd('');
      setPickStep('start');
      return;
    }
    setStart(s);
    setEnd(e2);
    setPickStep('start');
    setHoverDate('');
  };

  const handleDayClick = (dateObj) => {
    if (!dateObj) return;
    const str = toDateStr(dateObj);
    setPreset('custom');
    if (pickStep === 'start' || (start && end)) {
      setStart(str);
      setEnd('');
      setPickStep('end');
    } else {
      if (str < start) {
        setStart(str);
        setEnd(start);
      } else {
        setEnd(str);
      }
      setPickStep('start');
      setHoverDate('');
    }
  };

  const handleApply = () => {
    let applyStart = start;
    let applyEnd = end;
    if (start && !end) applyEnd = start;
    onApply(applyStart, applyEnd, preset);
  };

  const handleClear = () => {
    setStart('');
    setEnd('');
    setPreset('custom');
    setPickStep('start');
    setHoverDate('');
    onApply('', '', 'custom');
  };

  const presets = [
    { key: 'today', label: 'Today' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: '7d', label: 'Last 7 Days' },
    { key: '30d', label: 'Last 30 Days' },
    { key: 'this_month', label: 'This Month' },
    { key: 'custom', label: 'Custom' },
  ];

  const popupElement = (
    <div className="dfd-overlay" onClick={onClose}>
      <div className="dfd-popup" onClick={(ev) => ev.stopPropagation()}>
        <div className="dfd-header">
          <div>
            <div className="dfd-header-title">Filter &amp; Export</div>
            <div className="dfd-header-sub">Select a date range to filter payments</div>
          </div>
          <button type="button" className="dfd-close-btn" onClick={onClose}>×</button>
        </div>

        <div className="dfd-body">
          <div className="dfd-calendars">
            <div className="dfd-range-display">
              <div
                className={'dfd-range-field' + (pickStep === 'start' ? ' is-picking' : '')}
                onClick={() => setPickStep('start')}
              >
                <div className="dfd-range-field-label">Start Date</div>
                <div className="dfd-range-field-value">{start ? fmtDisp(start) : 'mm/dd/yyyy'}</div>
              </div>
              <div className="dfd-range-sep">
                <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </div>
              <div
                className={'dfd-range-field' + (pickStep === 'end' ? ' is-picking' : '')}
                onClick={() => setPickStep('end')}
              >
                <div className="dfd-range-field-label">End Date</div>
                <div className="dfd-range-field-value">{end ? fmtDisp(end) : 'mm/dd/yyyy'}</div>
              </div>
            </div>

            <div className="dfd-dual-cal" onMouseLeave={() => setHoverDate('')}>
              <MiniCalendar
                year={leftYear}
                month={leftMonth}
                start={start}
                end={end}
                hoverDate={hoverDate}
                onDayClick={handleDayClick}
                onDayHover={(s) => { if (pickStep === 'end' && start) setHoverDate(s); }}
                onPrev={goLeft}
                onNext={null}
                showPrev={true}
                showNext={false}
              />
              <div className="dfd-cal-divider" />
              <MiniCalendar
                year={rightYear}
                month={rightMonth}
                start={start}
                end={end}
                hoverDate={hoverDate}
                onDayClick={handleDayClick}
                onDayHover={(s) => { if (pickStep === 'end' && start) setHoverDate(s); }}
                onPrev={null}
                onNext={goRight}
                showPrev={false}
                showNext={true}
              />
            </div>

            {(start || end) && !hasRecords && (
              <div className="dfd-no-data-notice">
                <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <circle cx={12} cy={12} r={10} />
                  <line x1={12} y1={8} x2={12} y2={12} />
                  <line x1={12} y1={16} x2={12.01} y2={16} />
                </svg>
                No payment records available for this date range
              </div>
            )}
          </div>

          <div className="dfd-presets">
            <div className="dfd-presets-title">Quick Select</div>
            {presets.map((p) => (
              <button
                key={p.key}
                type="button"
                className={'dfd-preset-btn' + (preset === p.key ? ' is-active' : '')}
                onClick={() => applyPreset(p.key)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div className="dfd-footer">
          {onDownloadCSV ? (
            <button type="button" className="dfd-export-btn" onClick={onDownloadCSV}>
              <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1={12} y1={15} x2={12} y2={3} />
              </svg>
              Download Excel
            </button>
          ) : (
            <div style={{ flex: 1 }} />
          )}
          <div className="dfd-action-btns">
            <button type="button" className="dfd-clear-btn" onClick={handleClear}>Clear</button>
            <button
              type="button"
              className="dfd-apply-btn"
              onClick={handleApply}
              disabled={!start && !end}
            >
              <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' && document.body
    ? createPortal(popupElement, document.body)
    : popupElement;
};

const Pagination = ({ currentPage, totalPages, onPageChange, totalItems, itemsPerPage = ITEMS_PER_PAGE }) => {
  if (totalItems === 0) return null;
  const safeCurrentPage = Math.min(Math.max(1, currentPage), Math.max(1, totalPages));
  const startItem = (safeCurrentPage - 1) * itemsPerPage + 1;
  const endItem   = Math.min(safeCurrentPage * itemsPerPage, totalItems);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, safeCurrentPage - Math.floor(maxVisible / 2));
    let end   = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="pay-pagination">
      <div className="pay-pagination-info">
        Showing <strong>{startItem}–{endItem}</strong> of <strong>{totalItems}</strong> transactions
      </div>
      <div className="pay-pagination-controls">
        <button
          type="button"
          className="pay-page-btn"
          onClick={() => onPageChange(1)}
          disabled={safeCurrentPage <= 1}
          title="First page"
        >
          «
        </button>
        <button
          type="button"
          className="pay-page-btn"
          onClick={() => onPageChange(safeCurrentPage - 1)}
          disabled={safeCurrentPage <= 1}
          title="Previous page"
        >
          ‹
        </button>
        {getPageNumbers().map(p => (
          <button
            key={p}
            type="button"
            className={`pay-page-btn ${p === safeCurrentPage ? 'is-active' : ''}`}
            onClick={() => onPageChange(p)}
          >
            {p}
          </button>
        ))}
        <button
          type="button"
          className="pay-page-btn"
          onClick={() => onPageChange(safeCurrentPage + 1)}
          disabled={safeCurrentPage >= totalPages}
          title="Next page"
        >
          ›
        </button>
        <button
          type="button"
          className="pay-page-btn"
          onClick={() => onPageChange(totalPages)}
          disabled={safeCurrentPage >= totalPages}
          title="Last page"
        >
          »
        </button>
      </div>
    </div>
  );
};

const CustomSelect = ({ value, onChange, options, disabled, triggerClassName }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = React.useRef();
  const listRef = React.useRef();

  const openList = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const handler = (ev) => {
      if (triggerRef.current && triggerRef.current.contains(ev.target)) return;
      if (listRef.current && listRef.current.contains(ev.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    const reposition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
      }
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      document.removeEventListener('mousedown', handler);
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open]);

  const selected = options.find(o => o.value === value);

  return (
    <div style={{ position: 'relative', display: 'inline-block', width: triggerClassName === 'pay-cs-filter-trigger' ? 'auto' : '100%' }}>
      <button
        type="button"
        ref={triggerRef}
        className={'pay-cs-trigger ' + (triggerClassName || '')}
        disabled={disabled}
        onClick={() => { if (disabled) return; open ? setOpen(false) : openList(); }}
      >
        <span>{selected ? selected.label : 'Select'}</span>
        <span style={{ color: '#2d5f6e', marginLeft: 8, display: 'inline-flex' }}>
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </span>
      </button>
      {open && createPortal(
        <div
          ref={listRef}
          style={{
            position: 'fixed', top: pos.top, left: pos.left, width: pos.width,
            background: '#ffffff', border: '1px solid rgba(62,124,140,0.22)',
            borderRadius: 10, zIndex: 999999,
            boxShadow: '0 16px 40px rgba(15,23,42,0.16)',
          }}
        >
          {options.map(o => {
            const isSel = o.value === value;
            return (
              <div
                key={o.value}
                onClick={() => { onChange(o.value); setOpen(false); }}
                style={{
                  padding: '9px 12px', fontSize: 13, cursor: 'pointer',
                  color: isSel ? '#ffffff' : '#102a33',
                  background: isSel ? '#3E7C8C' : '#ffffff',
                  fontFamily: "'Outfit',sans-serif",
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(ev) => { if (!isSel) ev.currentTarget.style.background = '#eef8fa'; }}
                onMouseLeave={(ev) => { if (!isSel) ev.currentTarget.style.background = '#ffffff'; }}
              >
                {o.label}
              </div>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
};

const INR = '\u20B9';
const EMPTY_TEXT = '-';

// Data parsing helpers for Cashfree Payment API response
const getCustomerName = (payment) => {
  if (!payment) return 'Guest';
  const c = payment.customer || payment.user || payment.customerId;
  if (c && typeof c === 'object') {
    if (c.name) return c.name;
    if (c.fullName) return c.fullName;
    if (c.firstName) return c.firstName + (c.lastName ? ' ' + c.lastName : '');
  }
  if (payment.customerName) return payment.customerName;
  return 'Guest';
};

const getCustomerEmail = (payment) => {
  if (!payment) return EMPTY_TEXT;
  const c = payment.customer || payment.user || payment.customerId;
  if (c && typeof c === 'object' && c.email) return c.email;
  return payment.customerEmail || payment.email || EMPTY_TEXT;
};

const getCustomerPhone = (payment) => {
  if (!payment) return EMPTY_TEXT;
  const c = payment.customer || payment.user || payment.customerId;
  if (c && typeof c === 'object') {
    if (c.phone) return c.phone;
    if (c.mobileNumber) return c.mobileNumber;
    if (c.mobile) return c.mobile;
  }
  return payment.customerPhone || payment.phone || payment.mobile || EMPTY_TEXT;
};

const getCustomerId = (payment) => {
  if (!payment) return EMPTY_TEXT;
  const c = payment.customer || payment.user || payment.customerId;
  if (c && typeof c === 'object') return c._id || c.id || EMPTY_TEXT;
  if (typeof c === 'string') return c;
  return EMPTY_TEXT;
};

const getOrderId = (payment) => {
  if (!payment) return EMPTY_TEXT;
  if (payment.order && typeof payment.order === 'object') {
    return payment.order._id || payment.order.id || EMPTY_TEXT;
  }
  if (typeof payment.order === 'string' && payment.order) return payment.order;
  return payment.orderId || EMPTY_TEXT;
};

const getCfOrderId = (payment) => {
  if (!payment) return EMPTY_TEXT;
  return payment.cfOrderId || payment.cashfreeOrderId || payment.order?.cashfreeOrderId || EMPTY_TEXT;
};

const getCfPaymentId = (payment) => {
  if (!payment) return EMPTY_TEXT;
  return payment.cfPaymentId || payment.cashfreePaymentId || payment.order?.cashfreePaymentId || payment.transactionId || EMPTY_TEXT;
};

const getGateway = (payment) => {
  if (!payment) return 'CASHFREE';
  return (payment.gateway || payment.order?.paymentGateway || 'CASHFREE').toUpperCase();
};

const getAmount = (payment) => {
  if (!payment) return 0;
  if (payment.amount !== undefined && payment.amount !== null) return Number(payment.amount) || 0;
  if (payment.order && typeof payment.order === 'object') {
    return Number(payment.order.finalPayableAmount || payment.order.totalAmount || 0);
  }
  return 0;
};

const getCurrency = (payment) => {
  return payment?.currency || 'INR';
};

const getNormalizedStatus = (payment) => {
  const s = String(payment?.status || payment?.paymentStatus || payment?.order?.paymentStatus || 'PENDING').toUpperCase();
  if (['SUCCESS', 'PAID', 'COMPLETED', 'CAPTURED'].includes(s)) return 'SUCCESS';
  if (['PENDING'].includes(s)) return 'PENDING';
  if (['FAILED', 'CANCELLED', 'USER_DROPPED'].includes(s)) return 'FAILED';
  return s;
};

const fmtDate = (d) => {
  if (!d) return EMPTY_TEXT;
  const dateObj = new Date(d);
  if (isNaN(dateObj.getTime())) return EMPTY_TEXT;
  return dateObj.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtDateTime = (d) => {
  if (!d) return EMPTY_TEXT;
  const dateObj = new Date(d);
  if (isNaN(dateObj.getTime())) return EMPTY_TEXT;
  return dateObj.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true
  });
};

const fmtAmount = (n) => {
  if (n === undefined || n === null || isNaN(Number(n))) return EMPTY_TEXT;
  return INR + Number(n).toLocaleString('en-IN');
};

const resolveOrderImageUrl = (value) => {
  if (!value) return '';
  if (typeof value === 'string' && (value.trim().startsWith('[') || value.trim().startsWith('{'))) {
    try {
      const parsed = JSON.parse(value);
      return resolveOrderImageUrl(Array.isArray(parsed) ? parsed[0] : parsed);
    } catch (e) { return ''; }
  }
  if (Array.isArray(value)) return resolveOrderImageUrl(value[0]);
  if (value && typeof value === 'object') {
    return resolveOrderImageUrl(value.url || value.src || value.path || value.image || value.imageUrl || value.filename || value.secure_url || value.location);
  }
  if (typeof value !== 'string' || !value.trim()) return '';
  const imagePath = value.trim().replace(/\\/g, '/');
  if (imagePath.startsWith('blob:') || imagePath.startsWith('data:')) return imagePath;
  const base = String(IMAGE_BASE_URL || 'https://api.ishmaijewellery.com').replace(/\/+$/, '');
  if (/^https?:\/\//i.test(imagePath)) {
    const uploadIndex = imagePath.indexOf('/uploads/');
    return uploadIndex === -1 ? imagePath : base + imagePath.substring(uploadIndex);
  }
  const cleanPath = imagePath.replace(/^\/+/, '').replace(/^api\//, '');
  return base + '/' + cleanPath;
};

const getItemImage = (item) => {
  const prod = item?.product || item?.productId || item?.productDetails || {};
  const image = prod.image || prod.imageUrl || prod.image_url || prod.imageURL || prod.imagePath || prod.photo || prod.picture || (prod.images && prod.images[0]) || (prod.photos && prod.photos[0]) || prod.thumbnail || item.image || item.imageUrl || item.image_url || item.imageURL || (item.images && item.images[0]) || item.thumbnail || item.productImage || '';
  return resolveOrderImageUrl(image);
};

const getItemName = (item, idx) => {
  const prod = item?.product || item?.productId || item?.productDetails || {};
  return prod.name || prod.title || item.name || item.title || item.productName || `Product #${idx + 1}`;
};

const getItemPrice = (item) => {
  const prod = item?.product || item?.productId || item?.productDetails || {};
  const p = Number(item.price ?? item.unitPrice ?? prod.price ?? prod.finalPrice ?? 0);
  return isNaN(p) ? 0 : p;
};

const getItemQuantity = (item) => {
  const q = Number(item.quantity ?? item.qty ?? 1);
  return isNaN(q) || q < 1 ? 1 : q;
};

const getItemVariant = (item) => {
  const prod = item?.product || item?.productId || item?.productDetails || {};
  return item.variant || item.variantName || item.size || prod.variant || prod.size || '';
};

const getItemSku = (item) => {
  const prod = item?.product || item?.productId || item?.productDetails || {};
  return prod.sku || item.sku || (prod._id ? `#${String(prod._id).slice(-6).toUpperCase()}` : '');
};

const getShippingAddress = (order) => {
  if (!order) return null;
  const addr = order.shippingAddress || order.deliveryAddress || order.address;
  if (!addr) return null;
  if (typeof addr === 'string' && addr.trim()) {
    return { raw: addr.trim() };
  }
  if (typeof addr === 'object') {
    const line1 = addr.address || addr.addressLine1 || addr.line1 || addr.street || '';
    const line2 = addr.address2 || addr.addressLine2 || addr.line2 || addr.landmark || '';
    const city = addr.city || '';
    const state = addr.state || '';
    const pincode = addr.pincode || addr.pinCode || addr.postalCode || addr.zip || '';
    const name = addr.name || addr.fullName || addr.customerName || '';
    const phone = addr.phone || addr.mobile || '';
    if (!line1 && !city && !pincode && !name) return null;
    return { line1, line2, city, state, pincode, name, phone };
  }
  return null;
};

const ProductThumb = ({ src, name, size = 52 }) => {
  const [err, setErr] = useState(false);
  if (!src || err) {
    return (
      <div style={{
        width: size,
        height: size,
        borderRadius: 10,
        flexShrink: 0,
        background: 'rgba(109, 40, 217, 0.08)',
        border: '1px solid rgba(109, 40, 217, 0.18)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 20,
        color: '#6D28D9'
      }}>
        💎
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={name || 'Product'}
      onError={() => setErr(true)}
      style={{
        width: size,
        height: size,
        borderRadius: 10,
        objectFit: 'contain',
        flexShrink: 0,
        border: '1px solid #e2e8f0',
        background: '#ffffff',
        padding: 3,
        boxSizing: 'border-box'
      }}
    />
  );
};

const CARD_FILTERS = {
  all:     { label: 'Total Payments', filterGateway: 'all', filterStatus: 'all' },
  success: { label: 'Collected Revenue', filterGateway: 'all', filterStatus: 'SUCCESS' },
  pending: { label: 'Pending Payments', filterGateway: 'all', filterStatus: 'PENDING' },
  failed:  { label: 'Failed Payments', filterGateway: 'all', filterStatus: 'FAILED' },
};

const Payments = () => {
  const [payments, setPayments]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [filterGateway, setFilterGateway] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage]   = useState(1);
  const [selPayment, setSelPayment]     = useState(null);
  const [orderDetail, setOrderDetail]   = useState(null);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [activeCard, setActiveCard]     = useState('all');
  const [dateFrom, setDateFrom]         = useState('');
  const [dateTo, setDateTo]             = useState('');
  const [dateFilterOpen, setDateFilterOpen] = useState(false);
  const [datePreset, setDatePreset]     = useState('today');
  const [copiedKey, setCopiedKey]       = useState('');

  const [columnSort, setColumnSort]     = useState(null);

  const toggleColumnSort = (key) => {
    setColumnSort(prev => {
      if (prev?.key === key) return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      const defaultDir = key === 'customer' ? 'asc' : 'desc';
      return { key, dir: defaultDir };
    });
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAllPayments();
      const raw = res?.data?.data || res?.data?.payments || res?.data || [];
      const list = Array.isArray(raw) ? raw : [];
      const sorted = list
        .filter(p => p && !p.isDeleted)
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      setPayments(sorted);
    } catch (err) {
      console.error('Payments load error:', err);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { setCurrentPage(1); }, [search, filterGateway, filterStatus, activeCard, dateFrom, dateTo, columnSort]);

  useEffect(() => {
    if (!selPayment) {
      setOrderDetail(null);
      setLoadingOrder(false);
      return;
    }

    let isMounted = true;
    const orderObj = typeof selPayment.order === 'object' && selPayment.order ? selPayment.order : null;
    const existingItems = orderObj?.products || orderObj?.items || selPayment.products || selPayment.items;

    if (Array.isArray(existingItems) && existingItems.length > 0 && typeof existingItems[0]?.product === 'object') {
      setOrderDetail(orderObj);
      return;
    }

    const orderId = getOrderId(selPayment);
    if (!orderId || orderId === EMPTY_TEXT) {
      setOrderDetail(orderObj);
      return;
    }

    setLoadingOrder(true);
    getOrderById(orderId)
      .then(async (res) => {
        if (!isMounted) return;
        const fetchedOrder = res?.data?.data || res?.data?.order || res?.data || orderObj;
        if (!fetchedOrder) {
          setOrderDetail(orderObj);
          return;
        }

        const items = fetchedOrder.products || fetchedOrder.items || [];
        const missingIds = items
          .map(it => (typeof it.product === 'string' ? it.product : typeof it.productId === 'string' ? it.productId : null))
          .filter(Boolean);

        if (missingIds.length > 0 && typeof getProductById === 'function') {
          const productResults = await Promise.allSettled(missingIds.map(id => getProductById(id)));
          const prodMap = {};
          productResults.forEach((r, idx) => {
            if (r.status === 'fulfilled') {
              const pData = r.value?.data?.data || r.value?.data?.product || r.value?.data;
              if (pData) {
                const pid = pData._id || pData.id || missingIds[idx];
                prodMap[pid] = pData;
              }
            }
          });

          const enrichedItems = items.map(it => {
            const pid = typeof it.product === 'string' ? it.product : typeof it.productId === 'string' ? it.productId : null;
            if (pid && prodMap[pid]) {
              return {
                ...it,
                product: prodMap[pid],
              };
            }
            return it;
          });

          fetchedOrder.products = enrichedItems;
        }

        if (isMounted) {
          setOrderDetail(fetchedOrder);
        }
      })
      .catch((err) => {
        console.warn('Could not fetch full order details:', err);
        if (isMounted) {
          setOrderDetail(orderObj);
        }
      })
      .finally(() => {
        if (isMounted) setLoadingOrder(false);
      });

    return () => {
      isMounted = false;
    };
  }, [selPayment]);

  // Aggregated Stats
  const successfulPayments = payments.filter(p => getNormalizedStatus(p) === 'SUCCESS');
  const pendingPayments    = payments.filter(p => getNormalizedStatus(p) === 'PENDING');
  const failedPayments     = payments.filter(p => getNormalizedStatus(p) === 'FAILED');
  const totalCollectedRev  = successfulPayments.reduce((s, p) => s + getAmount(p), 0);
  const pendingRevenue     = pendingPayments.reduce((s, p) => s + getAmount(p), 0);

  const handleCardClick = (cardKey) => {
    setActiveCard(cardKey);
    const cf = CARD_FILTERS[cardKey];
    if (cf) {
      setFilterGateway(cf.filterGateway);
      setFilterStatus(cf.filterStatus);
    }
    setCurrentPage(1);
  };

  const formatDateLabel = (from, to) => {
    if (!from && !to) return 'Date range';
    if (from && !to) return fmtDisp(from);
    if (!from && to) return fmtDisp(to);
    if (from === to) return fmtDisp(from);
    return `${fmtDisp(from)} - ${fmtDisp(to)}`;
  };

  const handleDateFilterClear = () => {
    setDateFrom('');
    setDateTo('');
    setDatePreset('custom');
    setCurrentPage(1);
    setDateFilterOpen(false);
  };

  const handleCopy = (text, keyName) => {
    if (!text || text === EMPTY_TEXT) return;
    navigator.clipboard?.writeText(text);
    setCopiedKey(keyName);
    setTimeout(() => setCopiedKey(''), 1500);
  };

  const buildExportRows = (itemsToExport) => {
    const rows = [
      ['Cashfree Payments Export - ISHMAI Admin'],
      ['Period', dateFrom || dateTo ? formatDateLabel(dateFrom, dateTo) : 'All time'],
      ['Gateway', filterGateway === 'all' ? 'All Gateways' : filterGateway],
      ['Status', filterStatus === 'all' ? 'All Status' : filterStatus],
      ['Search', search ? search : 'All'],
      ['Total Collected (Success)', `'${fmtAmount(totalCollectedRev)}`],
      ['Exported at', new Date().toLocaleString('en-IN', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit', hour12:true })],
      [],
      [
        'Payment ID',
        'Order ID',
        'Cashfree Order ID',
        'Cashfree Payment ID',
        'Gateway',
        'Customer Name',
        'Customer Email',
        'Customer Phone',
        'Amount',
        'Currency',
        'Status',
        'Payment Method',
        'Created Date & Time'
      ],
    ];

    itemsToExport.forEach((p) => {
      rows.push([
        p._id || '',
        getOrderId(p),
        getCfOrderId(p),
        getCfPaymentId(p),
        getGateway(p),
        getCustomerName(p),
        getCustomerEmail(p),
        getCustomerPhone(p),
        getAmount(p),
        getCurrency(p),
        getNormalizedStatus(p),
        p.paymentMethod || 'ONLINE',
        fmtDateTime(p.createdAt),
      ]);
    });

    return rows;
  };

  const downloadExcel = async (rows, filename) => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Payments');

    const headerRowIndex = rows.findIndex(r => r[0] === 'Payment ID');

    rows.forEach((rowData, idx) => {
      const row = sheet.addRow(rowData);

      if (idx === 0) {
        row.font = { bold: true, size: 14, color: { argb: 'FF102A33' } };
      } else if (idx === headerRowIndex) {
        row.eachCell(cell => {
          cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FF2D1B69' }
          };
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
          cell.border = {
            top: { style: 'thin' }, left: { style: 'thin' },
            bottom: { style: 'thin' }, right: { style: 'thin' },
          };
        });
      } else if (idx > headerRowIndex) {
        row.eachCell(cell => {
          cell.border = {
            top: { style: 'thin', color: { argb: 'FFD9D9D9' } },
            left: { style: 'thin', color: { argb: 'FFD9D9D9' } },
            bottom: { style: 'thin', color: { argb: 'FFD9D9D9' } },
            right: { style: 'thin', color: { argb: 'FFD9D9D9' } },
          };
        });
      } else if (idx >= 1 && idx <= 5) {
        sheet.getCell(`A${idx + 1}`).font = { bold: true };
      }
    });

    sheet.columns = [
      { width: 26 }, { width: 26 }, { width: 34 }, { width: 34 },
      { width: 14 }, { width: 22 }, { width: 24 }, { width: 16 },
      { width: 14 }, { width: 10 }, { width: 14 }, { width: 16 }, { width: 24 },
    ];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportCSV = async () => {
    if (loading) {
      alert('Data is still loading. Please wait a moment and try again.');
      return;
    }
    if (!filtered.length) {
      alert('No transactions match the current filters to export.');
      return;
    }
    const rows = buildExportRows(sortedFiltered);
    await downloadExcel(rows, `cashfree-payments-${dateFrom || 'all'}-${dateTo || 'all'}.xlsx`);
  };

  // Filter Logic
  const filtered = payments.filter(p => {
    const q = search.toLowerCase().trim();
    const pId = String(p._id || '').toLowerCase();
    const orderId = String(getOrderId(p)).toLowerCase();
    const cfOrderId = String(getCfOrderId(p)).toLowerCase();
    const cfPaymentId = String(getCfPaymentId(p)).toLowerCase();
    const name = String(getCustomerName(p)).toLowerCase();
    const email = String(getCustomerEmail(p)).toLowerCase();
    const phone = String(getCustomerPhone(p)).toLowerCase();
    const gateway = String(getGateway(p)).toLowerCase();
    const status = String(getNormalizedStatus(p)).toLowerCase();
    const amountStr = String(getAmount(p));

    const matchSearch = !q
      || pId.includes(q)
      || orderId.includes(q)
      || cfOrderId.includes(q)
      || cfPaymentId.includes(q)
      || name.includes(q)
      || email.includes(q)
      || phone.includes(q)
      || gateway.includes(q)
      || status.includes(q)
      || amountStr.includes(q);

    const matchGateway = filterGateway === 'all'
      ? true
      : gateway === filterGateway.toLowerCase();

    const matchStatus = filterStatus === 'all'
      ? true
      : status === filterStatus.toLowerCase();

    if (!matchSearch || !matchGateway || !matchStatus) return false;

    if (!dateFrom && !dateTo) return true;
    const created = new Date(p.createdAt || 0);
    if (isNaN(created.getTime())) return false;
    if (dateFrom) {
      const start = new Date(dateFrom + 'T00:00:00');
      if (created < start) return false;
    }
    if (dateTo) {
      const end = new Date(dateTo + 'T23:59:59');
      if (created > end) return false;
    }
    return true;
  });

  const sortedFiltered = [...filtered];
  if (columnSort) {
    const dirMul = columnSort.dir === 'asc' ? 1 : -1;
    if (columnSort.key === 'customer') {
      sortedFiltered.sort((a, b) => dirMul * getCustomerName(a).localeCompare(getCustomerName(b)));
    } else if (columnSort.key === 'amount') {
      sortedFiltered.sort((a, b) => dirMul * (getAmount(a) - getAmount(b)));
    } else if (columnSort.key === 'date') {
      sortedFiltered.sort((a, b) => dirMul * (new Date(a.createdAt || 0) - new Date(b.createdAt || 0)));
    }
  }

  const totalPages = Math.max(1, Math.ceil(sortedFiltered.length / ITEMS_PER_PAGE));
  const pageStart  = (currentPage - 1) * ITEMS_PER_PAGE;
  const pageItems  = sortedFiltered.slice(pageStart, pageStart + ITEMS_PER_PAGE);

  // 4 Balanced Cards fitting the 4-column StatCards component natively
  const statsData = [
    { key: 'all',     val: payments.length,           lbl: 'Total Payments',    icon: 'credit', desc: 'All transactions' },
    { key: 'success', val: successfulPayments.length, lbl: 'Collected Revenue', icon: 'check',  desc: fmtAmount(totalCollectedRev) + ' collected' },
    { key: 'pending', val: pendingPayments.length,    lbl: 'Pending Payments',  icon: 'clock',  desc: fmtAmount(pendingRevenue) + ' awaiting' },
    { key: 'failed',  val: failedPayments.length,     lbl: 'Failed Payments',   icon: 'alert',  desc: 'Failed attempts' },
  ];

  // Helper for Status Badge Component
  const renderStatusBadge = (statusStr) => {
    const s = (statusStr || 'PENDING').toUpperCase();
    if (s === 'SUCCESS') {
      return (
        <span className="cf-status-badge cf-status-success">
          <span className="cf-dot" /> SUCCESS
        </span>
      );
    }
    if (s === 'PENDING') {
      return (
        <span className="cf-status-badge cf-status-pending">
          <span className="cf-dot" /> PENDING
        </span>
      );
    }
    return (
      <span className="cf-status-badge cf-status-failed">
        <span className="cf-dot" /> FAILED
      </span>
    );
  };

  // Helper for Gateway Badge Component
  const renderGatewayBadge = (gw) => {
    const isCf = String(gw).toUpperCase() === 'CASHFREE';
    return (
      <span className={`cf-gateway-badge ${isCf ? 'cf-gw-cashfree' : 'cf-gw-other'}`}>
        {isCf ? (
          <>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
            </svg>
            CASHFREE
          </>
        ) : (
          gw || 'COD'
        )}
      </span>
    );
  };

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap');
    
    .pay-shell {
      position: relative;
      padding: 4px 2px 24px;
      font-family: 'Outfit', sans-serif;
      color: #102a33;
    }
    .pay-stack { position: relative; z-index: 1; }

    /* Page Header */
    .page-header-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      margin-bottom: 16px;
      flex-wrap: wrap;
    }
    .page-header-main {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
    .page-header-title-row {
      display: flex;
      align-items: center;
      gap: 12px;
    }
    .page-header-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 42px;
      height: 42px;
      border-radius: 12px;
      background: rgba(109, 40, 217, 0.12);
      color: #7C3AED;
      box-shadow: inset 0 0 0 1px rgba(109, 40, 217, 0.22);
    }
    .page-header-icon svg { width: 22px; height: 22px; }
    .page-title {
      font-size: 1.55rem;
      font-weight: 800;
      color: #FFFFFF !important;
      margin: 0;
      letter-spacing: -0.3px;
      line-height: 1.2;
    }
    .header-subtitle {
      font-size: 13px;
      color: #C4B5FD;
      font-weight: 500;
    }

    .filter-export-btn {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 10px 18px;
      border-radius: 10px;
      border: 1px solid rgba(139, 92, 246, 0.4);
      background: linear-gradient(135deg, rgba(109, 40, 217, 0.35), rgba(124, 58, 237, 0.25));
      color: #FFFFFF;
      font-size: 13px;
      font-weight: 700;
      cursor: pointer;
      transition: all 0.18s ease;
      white-space: nowrap;
      font-family: 'Outfit', sans-serif;
      box-shadow: 0 4px 12px rgba(109, 40, 217, 0.2);
    }
    .filter-export-btn:hover:not(:disabled) {
      background: linear-gradient(135deg, rgba(109, 40, 217, 0.55), rgba(124, 58, 237, 0.45));
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(109, 40, 217, 0.3);
    }
    .filter-export-btn:disabled { opacity: 0.5; cursor: not-allowed; }

    /* Active Filter indicator */
    .active-filter-bar {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 12px;
      color: #C4B5FD;
      margin: 10px 0 16px;
    }
    .active-filter-tag {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 11px;
      font-weight: 700;
      background: rgba(123, 44, 255, 0.2);
      color: #C4B5FD;
      border: 1px solid rgba(168, 85, 247, 0.3);
    }
    .clear-filter-btn {
      background: none;
      border: none;
      cursor: pointer;
      font-size: 11px;
      color: #94A3B8;
      text-decoration: underline;
      font-family: 'Outfit', sans-serif;
      padding: 0;
      transition: color 0.15s;
    }
    .clear-filter-btn:hover { color: #EC4899; }

    /* Main Table Container (Card) */
    html body #root .wid {
      background: #FFFFFF !important;
      border: 1px solid rgba(62, 124, 140, 0.16) !important;
      border-radius: 16px !important;
      padding: 22px !important;
      box-shadow: 0 20px 48px rgba(6, 25, 54, 0.08) !important;
    }
    .wh {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 18px;
      flex-wrap: wrap;
      gap: 14px;
    }
    .wt {
      font-size: 15px;
      font-weight: 800;
      color: #102a33;
      letter-spacing: -0.2px;
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .filter-bar {
      display: flex;
      align-items: center;
      gap: 10px;
      flex-wrap: wrap;
    }
    .filter-input {
      padding: 9px 14px;
      border-radius: 10px;
      border: 1px solid #d8dee9;
      background: #FFFFFF;
      color: #102a33;
      font-family: 'Outfit', sans-serif;
      font-size: 13px;
      outline: none;
      min-width: 250px;
      box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
      transition: all 0.15s ease;
    }
    .filter-input:focus {
      border-color: #6D28D9;
      box-shadow: 0 0 0 3px rgba(109, 40, 217, 0.15);
    }
    .filter-input::placeholder { color: #94a3b8; }

    .filter-date-btn {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 9px 14px;
      border-radius: 10px;
      border: 1px solid rgba(62, 124, 140, 0.28);
      background: #FFFFFF;
      color: #102a33;
      font-size: 13px;
      font-weight: 700;
      cursor: pointer;
      transition: all 0.15s;
      white-space: nowrap;
      font-family: 'Outfit', sans-serif;
      box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
    }
    .filter-date-btn:hover {
      border-color: #3E7C8C;
      background: #f8fafc;
    }
    .filter-date-btn span { display: inline-flex; align-items: center; }

    .pay-cs-trigger {
      width: 100%;
      text-align: left;
      cursor: pointer;
      padding: 9px 14px;
      border-radius: 10px;
      border: 1px solid rgba(62, 124, 140, 0.28);
      background: #FFFFFF;
      color: #102a33;
      font-family: 'Outfit', sans-serif;
      font-size: 13px;
      font-weight: 700;
      display: flex;
      justify-content: space-between;
      align-items: center;
      box-sizing: border-box;
      box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
      transition: all 0.15s;
    }
    .pay-cs-trigger:hover {
      border-color: #3E7C8C;
      box-shadow: 0 0 0 3px rgba(62, 124, 140, 0.12);
    }
    .pay-cs-filter-trigger {
      width: auto;
      min-width: 140px;
    }

    /* Scrollable Table Container */
    .tbl-scroll-wrap {
      overflow-x: auto;
      border-radius: 12px;
      border: 1px solid rgba(62, 124, 140, 0.18);
      background: #FFFFFF;
      scrollbar-width: thin;
      scrollbar-color: rgba(62, 124, 140, 0.28) transparent;
    }
    .tbl-scroll-wrap::-webkit-scrollbar { height: 6px; }
    .tbl-scroll-wrap::-webkit-scrollbar-thumb { background: rgba(62, 124, 140, 0.28); border-radius: 99px; }

    .pay-tbl {
      width: 100%;
      border-collapse: separate;
      border-spacing: 0;
      font-family: 'Outfit', sans-serif;
      text-align: left;
    }
    .pay-tbl thead {
      background: #f8fafc;
    }
    .pay-tbl th {
      padding: 12px 14px;
      font-size: 10.5px;
      font-weight: 700;
      color: #2d5f6e;
      text-transform: uppercase;
      letter-spacing: 0.6px;
      border-bottom: 1px solid rgba(62, 124, 140, 0.18);
      white-space: nowrap;
      vertical-align: middle;
      background: #f8fafc;
    }
    .pay-tbl th.sortable {
      cursor: pointer;
      user-select: none;
      transition: color 0.15s;
    }
    .pay-tbl th.sortable:hover { color: #d4741a; }
    .sort-clear-icon { margin-left: 5px; cursor: pointer; opacity: 0.7; }
    .sort-clear-icon:hover { opacity: 1; color: #ec4899; }

    .pay-tbl td {
      padding: 13px 14px;
      font-size: 13px;
      color: #102a33;
      border-bottom: 1px solid rgba(62, 124, 140, 0.10);
      vertical-align: middle;
      white-space: nowrap;
      background: #FFFFFF;
    }
    .pay-tbl tbody tr {
      transition: background 0.15s ease;
    }
    .pay-tbl tbody tr:hover td {
      background: #f8fafc;
    }
    .pay-tbl tbody tr.row-selected td {
      background: #f1f5f9;
    }
    .pay-tbl tbody tr:last-child td {
      border-bottom: none;
    }

    /* Cell Elements */
    .cust-cell { display: flex; flex-direction: column; gap: 2px; }
    .cust-name { font-weight: 700; color: #102a33; font-size: 13px; }
    .cust-sub { font-size: 11px; color: #64748b; }

    .cf-code {
      font-family: monospace;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.2px;
    }
    .cf-code-payid { color: #0284c7; }
    .cf-code-order { color: #ec4899; }
    .cf-code-cforder { color: #64748b; font-size: 11px; }

    /* Badges */
    .cf-status-badge {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding: 4px 10px;
      border-radius: 20px;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.3px;
      text-transform: uppercase;
      white-space: nowrap;
    }
    .cf-status-success {
      background: #ecfdf5;
      color: #047857;
      border: 1px solid #a7f3d0;
    }
    .cf-status-pending {
      background: #fffbeb;
      color: #b45309;
      border: 1px solid #fde68a;
    }
    .cf-status-failed {
      background: #fef2f2;
      color: #b91c1c;
      border: 1px solid #fecaca;
    }
    .cf-dot {
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background: currentColor;
      display: inline-block;
    }

    .cf-gateway-badge {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding: 4px 10px;
      border-radius: 8px;
      font-weight: 800;
      font-size: 11px;
      letter-spacing: 0.4px;
      white-space: nowrap;
    }
    .cf-gw-cashfree {
      background: #eff6ff;
      color: #1d4ed8;
      border: 1px solid #bfdbfe;
    }
    .cf-gw-other {
      background: #f1f5f9;
      color: #475569;
      border: 1px solid #e2e8f0;
    }

    /* Action Buttons */
    .row-action-view {
      width: 32px;
      height: 32px;
      border-radius: 8px;
      border: 1px solid #bae6fd;
      background: #e0f2fe;
      color: #0284c7;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      transition: all 0.15s ease;
    }
    .row-action-view:hover {
      background: #bae6fd;
      color: #0369a1;
      transform: translateY(-1px);
      box-shadow: 0 2px 6px rgba(2, 132, 199, 0.2);
    }

    /* Pagination */
    .pay-pagination {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      margin-top: 18px;
      padding-top: 14px;
      border-top: 1px solid rgba(62, 124, 140, 0.12);
      flex-wrap: wrap;
    }
    .pay-pagination-info {
      font-size: 13px;
      color: #64748b;
      font-weight: 500;
    }
    .pay-pagination-info strong {
      color: #102a33;
      font-weight: 700;
    }
    .pay-pagination-controls {
      display: flex;
      align-items: center;
      gap: 5px;
    }
    .pay-page-btn {
      min-width: 32px;
      height: 32px;
      padding: 0 8px;
      border-radius: 8px;
      border: 1px solid rgba(62, 124, 140, 0.22);
      background: #FFFFFF;
      color: #102a33;
      font-size: 12.5px;
      font-weight: 700;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      transition: all 0.15s;
    }
    .pay-page-btn:hover:not(:disabled) {
      background: #f1f5f9;
      border-color: #6D28D9;
      color: #6D28D9;
    }
    .pay-page-btn.is-active {
      background: linear-gradient(135deg, #6D28D9, #A855F7);
      color: #FFFFFF;
      border-color: transparent;
      box-shadow: 0 2px 8px rgba(109, 40, 217, 0.3);
    }
    .pay-page-btn:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    /* Modal Styles */
    .modal-overlay {
      position: fixed;
      inset: 0;
      background: rgba(13, 6, 40, 0.72);
      backdrop-filter: blur(12px);
      z-index: 999999;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 24px;
    }
    .modal-box {
      background: #FFFFFF;
      border: 1px solid rgba(62, 124, 140, 0.2);
      border-radius: 20px;
      width: 100%;
      max-width: 800px;
      max-height: 90vh;
      overflow-y: auto;
      padding: 26px;
      box-shadow: 0 24px 64px rgba(15, 23, 42, 0.25);
      font-family: 'Outfit', sans-serif;
      color: #102a33;
    }
    .modal-box::-webkit-scrollbar {
      width: 6px;
    }
    .modal-box::-webkit-scrollbar-thumb {
      background: #cbd5e1;
      border-radius: 4px;
    }
    .modal-hdr {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 20px;
      padding-bottom: 14px;
      border-bottom: 1px solid #e5eaf0;
    }
    .modal-title {
      font-size: 19px;
      font-weight: 800;
      color: #102a33;
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .modal-close {
      background: #f1f5f9;
      border: 1px solid #cbd5e1;
      color: #475569;
      cursor: pointer;
      width: 32px;
      height: 32px;
      border-radius: 8px;
      font-size: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.15s;
    }
    .modal-close:hover {
      background: #fee2e2;
      color: #dc2626;
      border-color: #fca5a5;
    }

    .cf-modal-card {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 14px;
      padding: 16px 18px;
      margin-bottom: 14px;
    }
    .cf-modal-card-title {
      font-size: 11px;
      font-weight: 800;
      color: #2d5f6e;
      text-transform: uppercase;
      letter-spacing: 0.7px;
      margin-bottom: 12px;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    .cf-grid-2 {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 12px;
    }
    .cf-grid-full { grid-column: 1 / -1; }
    @media (max-width: 640px) { .cf-grid-2 { grid-template-columns: 1fr; } }

    .cf-field {
      display: flex;
      flex-direction: column;
      gap: 3px;
      min-width: 0;
    }
    .cf-field-label {
      font-size: 10px;
      font-weight: 700;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .cf-field-val {
      font-size: 13px;
      font-weight: 700;
      color: #102a33;
      word-break: break-all;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .cf-copy-btn {
      background: none;
      border: none;
      color: #94a3b8;
      cursor: pointer;
      padding: 2px;
      display: inline-flex;
      align-items: center;
      transition: color 0.15s;
    }
    .cf-copy-btn:hover { color: #0284c7; }
    .cf-copied-toast { font-size: 10px; color: #059669; font-weight: 700; }

    /* Product Details & Items */
    .cf-product-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    .cf-product-item {
      background: #ffffff;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      padding: 10px 14px;
      display: flex;
      align-items: center;
      gap: 14px;
      transition: border-color 0.15s, box-shadow 0.15s;
    }
    .cf-product-item:hover {
      border-color: #cbd5e1;
      box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
    }
    .cf-product-info {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
    .cf-product-title-row {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .cf-product-name {
      font-size: 13.5px;
      font-weight: 700;
      color: #0f172a;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .cf-product-meta-row {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 8px;
      font-size: 11.5px;
      color: #64748b;
    }
    .cf-product-badge {
      background: #eff6ff;
      color: #1d4ed8;
      border: 1px solid #dbeafe;
      padding: 1px 7px;
      border-radius: 6px;
      font-weight: 600;
      font-size: 11px;
    }
    .cf-product-sku {
      background: #f8fafc;
      color: #475569;
      border: 1px solid #e2e8f0;
      padding: 1px 6px;
      border-radius: 4px;
      font-family: monospace;
      font-size: 10.5px;
    }
    .cf-product-unit-price {
      font-weight: 600;
      color: #475569;
    }
    .cf-product-pricing {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 3px;
      flex-shrink: 0;
    }
    .cf-product-qty {
      font-size: 11px;
      font-weight: 700;
      color: #475569;
      background: #f1f5f9;
      padding: 2px 8px;
      border-radius: 6px;
    }
    .cf-product-total {
      font-size: 14px;
      font-weight: 800;
      color: #059669;
    }
    .cf-order-summary-box {
      background: #f1f5f9;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      padding: 12px 16px;
      margin-top: 12px;
      display: flex;
      flex-direction: column;
      gap: 6px;
    }
    .cf-summary-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 12.5px;
      color: #475569;
    }
    .cf-summary-total {
      border-top: 1px solid #cbd5e1;
      padding-top: 8px;
      margin-top: 4px;
      font-weight: 700;
      color: #0f172a;
    }

    .pay-loading {
      padding: 60px 0;
      display: flex;
      justify-content: center;
      align-items: center;
    }
    .pay-spinner {
      width: 32px;
      height: 32px;
      border: 3px solid rgba(109, 40, 217, 0.2);
      border-top-color: #6D28D9;
      border-radius: 50%;
      animation: paySpin 0.7s linear infinite;
    }
    @keyframes paySpin { to { transform: rotate(360deg); } }

    .pay-empty {
      text-align: center;
      padding: 44px 20px;
      color: #64748b;
      font-size: 13.5px;
    }
  `;

  return (
    <AdminLayout hideLayoutOnModal={!!selPayment}>
      <style>{css}</style>

      <div className="pay-shell">
        <div className="pay-stack">

          {/* Main Page Header */}
          <div className="page-header-row">
            <div className="page-header-main">
              <div className="page-header-title-row">
                <div className="page-header-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="5" width="20" height="14" rx="2" />
                    <path d="M2 10h20" />
                  </svg>
                </div>
                <div>
                  <h1 className="page-title">Payments</h1>
                  <div className="header-subtitle">
                    Track and review all Cashfree payment transactions across orders
                  </div>
                </div>
              </div>
            </div>
            <button
              type="button"
              className="filter-export-btn"
              disabled={loading}
              onClick={handleExportCSV}
            >
              {loading ? 'Loading...' : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1={12} y1={15} x2={12} y2={3} />
                  </svg>
                  Export Excel
                </>
              )}
            </button>
          </div>

          {/* 4 Clickable Stat Cards (single clean row with native gradients) */}
          <StatCards
            cards={statsData.map(s => ({
              title: s.lbl,
              value: s.val,
              description: s.desc,
              icon: s.icon,
              active: activeCard === s.key,
              onClick: () => handleCardClick(s.key),
            }))}
          />

          {/* Active filter indicator */}
          {activeCard !== 'all' && (
            <div className="active-filter-bar">
              <span>Filtered by:</span>
              <span className="active-filter-tag">
                {statsData.find(s => s.key === activeCard)?.lbl}
              </span>
              <button className="clear-filter-btn" onClick={() => handleCardClick('all')}>
                Clear filter
              </button>
            </div>
          )}

          {/* Table & Filters Card Container */}
          <div className="wid">
            <div className="wh">
              <span className="wt">
                <span>
                  {activeCard !== 'all'
                    ? statsData.find(s => s.key === activeCard)?.lbl
                    : 'All Payments'}
                </span>
                <span style={{ color: '#64748b', fontWeight: 600, fontSize: 13 }}>
                  ({sortedFiltered.length})
                </span>
              </span>

              <div className="filter-bar">
                <input
                  className="filter-input"
                  placeholder="Search by ID, Order, Customer, or Phone..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />

                <button
                  type="button"
                  className="filter-date-btn"
                  onClick={() => setDateFilterOpen(true)}
                >
                  <span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}>
                      <rect x="3" y="4" width="18" height="18" rx="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    {formatDateLabel(dateFrom, dateTo)}
                  </span>
                  {(dateFrom || dateTo) && (
                    <span
                      style={{ marginLeft: 8, fontSize: 13, cursor: 'pointer', opacity: 0.7 }}
                      onClick={(e) => { e.stopPropagation(); handleDateFilterClear(); }}
                      title="Clear date filter"
                    >
                      ✕
                    </span>
                  )}
                </button>

                <CustomSelect
                  triggerClassName="pay-cs-filter-trigger"
                  value={filterGateway}
                  onChange={val => {
                    setFilterGateway(val);
                    setActiveCard('all');
                  }}
                  options={[
                    { value: 'all', label: 'All Gateways' },
                    { value: 'CASHFREE', label: 'Cashfree' },
                  ]}
                />

                <CustomSelect
                  triggerClassName="pay-cs-filter-trigger"
                  value={filterStatus}
                  onChange={val => {
                    setFilterStatus(val);
                    if (val === 'SUCCESS') setActiveCard('success');
                    else if (val === 'PENDING') setActiveCard('pending');
                    else if (val === 'FAILED')  setActiveCard('failed');
                    else setActiveCard('all');
                  }}
                  options={[
                    { value: 'all', label: 'All Status' },
                    { value: 'SUCCESS', label: 'Success' },
                    { value: 'PENDING', label: 'Pending' },
                    { value: 'FAILED', label: 'Failed' },
                  ]}
                />
              </div>
            </div>

            {/* Dashboard-Style Date Filter Popup */}
            {dateFilterOpen && (
              <DateFilterPopup
                initialStart={dateFrom}
                initialEnd={dateTo}
                initialPreset={datePreset}
                onApply={(start, end, preset) => {
                  setDateFrom(start);
                  setDateTo(end);
                  setDatePreset(preset);
                  setDateFilterOpen(false);
                  setCurrentPage(1);
                }}
                onClose={() => setDateFilterOpen(false)}
                hasRecords={sortedFiltered.length > 0}
                onDownloadCSV={handleExportCSV}
              />
            )}

            {loading ? (
              <div className="pay-loading"><div className="pay-spinner" /></div>
            ) : (
              <>
                <div className="tbl-scroll-wrap">
                  <table className="pay-tbl">
                    <thead>
                      <tr>
                        <th style={{ width: 44, textAlign: 'center' }}>#</th>
                        <th style={{ minWidth: 150 }}>Payment &amp; CF Order ID</th>
                        <th style={{ minWidth: 100 }}>Order ID</th>
                        <th className="sortable" style={{ minWidth: 180 }} onClick={() => toggleColumnSort('customer')}>
                          Customer{columnSort?.key === 'customer' ? (columnSort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
                          {columnSort?.key === 'customer' && (
                            <span
                              className="sort-clear-icon"
                              onClick={(e) => { e.stopPropagation(); setColumnSort(null); }}
                              title="Clear sort"
                            >
                              ✕
                            </span>
                          )}
                        </th>
                        <th style={{ minWidth: 110 }}>Gateway</th>
                        <th style={{ minWidth: 140 }}>Cashfree Payment ID</th>
                        <th className="sortable" style={{ minWidth: 120 }} onClick={() => toggleColumnSort('amount')}>
                          Amount{columnSort?.key === 'amount' ? (columnSort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
                          {columnSort?.key === 'amount' && (
                            <span
                              className="sort-clear-icon"
                              onClick={(e) => { e.stopPropagation(); setColumnSort(null); }}
                              title="Clear sort"
                            >
                              ✕
                            </span>
                          )}
                        </th>
                        <th style={{ minWidth: 110 }}>Status</th>
                        <th className="sortable" style={{ minWidth: 130 }} onClick={() => toggleColumnSort('date')}>
                          Date &amp; Time{columnSort?.key === 'date' ? (columnSort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
                          {columnSort?.key === 'date' && (
                            <span
                              className="sort-clear-icon"
                              onClick={(e) => { e.stopPropagation(); setColumnSort(null); }}
                              title="Clear sort"
                            >
                              ✕
                            </span>
                          )}
                        </th>
                        <th style={{ width: 80, textAlign: 'center' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageItems.length === 0 ? (
                        <tr>
                          <td colSpan="10" className="pay-empty">
                            {payments.length === 0
                              ? 'No payment transactions found in the database.'
                              : 'No payment transactions match your search or filters.'}
                          </td>
                        </tr>
                      ) : (
                        pageItems.map((p, idx) => {
                          const status = getNormalizedStatus(p);
                          const cfOrderId = getCfOrderId(p);
                          const cfPayId = getCfPaymentId(p);
                          const orderId = getOrderId(p);

                          return (
                            <tr
                              key={p._id || idx}
                              className={selPayment && selPayment._id === p._id ? 'row-selected' : ''}
                              onClick={() => setSelPayment(p)}
                              style={{ cursor: 'pointer' }}
                            >
                              <td style={{ color: '#64748b', fontSize: 12, fontWeight: 700, textAlign: 'center' }}>
                                {pageStart + idx + 1}
                              </td>

                              {/* Payment ID & Cashfree Order ID stacked */}
                              <td>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                  <span className="cf-code cf-code-payid">
                                    {p._id ? '#' + p._id.slice(-8).toUpperCase() : EMPTY_TEXT}
                                  </span>
                                  {cfOrderId !== EMPTY_TEXT && (
                                    <span className="cf-code cf-code-cforder" title={cfOrderId}>
                                      {cfOrderId.length > 22 ? cfOrderId.slice(0, 20) + '...' : cfOrderId}
                                    </span>
                                  )}
                                </div>
                              </td>

                              {/* Order ID */}
                              <td>
                                <span className="cf-code cf-code-order">
                                  {orderId !== EMPTY_TEXT ? '#' + (orderId.length > 8 ? orderId.slice(-8).toUpperCase() : orderId) : EMPTY_TEXT}
                                </span>
                              </td>

                              {/* Customer */}
                              <td>
                                <div className="cust-cell">
                                  <span className="cust-name">{getCustomerName(p)}</span>
                                  <span className="cust-sub">{getCustomerEmail(p)}</span>
                                  {getCustomerPhone(p) !== EMPTY_TEXT && (
                                    <span className="cust-sub">{getCustomerPhone(p)}</span>
                                  )}
                                </div>
                              </td>

                              {/* Gateway */}
                              <td>
                                {renderGatewayBadge(getGateway(p))}
                              </td>

                              {/* Cashfree Payment ID */}
                              <td>
                                <span className="cf-code" style={{ color: '#475569' }} title={cfPayId}>
                                  {cfPayId !== EMPTY_TEXT ? (cfPayId.length > 18 ? cfPayId.slice(0, 16) + '...' : cfPayId) : EMPTY_TEXT}
                                </span>
                              </td>

                              {/* Amount */}
                              <td>
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                                  <span style={{ color: '#059669', fontWeight: 800, fontSize: 13.5 }}>
                                    {fmtAmount(getAmount(p))}
                                  </span>
                                  <span style={{ color: '#94a3b8', fontSize: 11, fontWeight: 600 }}>
                                    {getCurrency(p)}
                                  </span>
                                </div>
                              </td>

                              {/* Status */}
                              <td>
                                {renderStatusBadge(status)}
                              </td>

                              {/* Date & Time */}
                              <td>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                  <span style={{ color: '#102a33', fontWeight: 600, fontSize: 12 }}>
                                    {fmtDate(p.createdAt)}
                                  </span>
                                  <span style={{ color: '#64748b', fontSize: 11 }}>
                                    {p.createdAt ? new Date(p.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : ''}
                                  </span>
                                </div>
                              </td>

                              {/* Actions */}
                              <td style={{ textAlign: 'center' }}>
                                <button
                                  type="button"
                                  className="row-action-view"
                                  title="View Details"
                                  onClick={(e) => { e.stopPropagation(); setSelPayment(p); }}
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                    <circle cx="12" cy="12" r="3"/>
                                  </svg>
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                  totalItems={sortedFiltered.length}
                />
              </>
            )}
          </div>

        </div>
      </div>

      {/* Cashfree Payment Details View Modal */}
      {selPayment && (
        <div className="modal-overlay" onClick={e => e.target.classList.contains('modal-overlay') && setSelPayment(null)}>
          <div className="modal-box">
            <div className="modal-hdr">
              <div className="modal-title">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#6D28D9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="5" width="20" height="14" rx="2" />
                  <path d="M2 10h20" />
                </svg>
                <span>Payment Details</span>
                {renderGatewayBadge(getGateway(selPayment))}
              </div>
              <button className="modal-close" onClick={() => setSelPayment(null)} title="Close">✕</button>
            </div>

            {/* Top Highlight Banner */}
            <div style={{
              background: 'linear-gradient(135deg, #f0fdf4, #eff6ff)',
              border: '1px solid #bbf7d0',
              borderRadius: 14,
              padding: '16px 20px',
              marginBottom: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 12,
            }}>
              <div>
                <div style={{ fontSize: 11, color: '#475569', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5 }}>
                  Total Payment Amount
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#059669', letterSpacing: -0.3 }}>
                  {fmtAmount(getAmount(selPayment))} <span style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>{getCurrency(selPayment)}</span>
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#475569', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5, marginBottom: 4 }}>
                  Payment Status
                </div>
                {renderStatusBadge(getNormalizedStatus(selPayment))}
              </div>
            </div>

            {/* Cashfree Transaction Information Card */}
            <div className="cf-modal-card">
              <div className="cf-modal-card-title">
                <span>Cashfree Transaction Information</span>
                {renderGatewayBadge(getGateway(selPayment))}
              </div>
              <div className="cf-grid-2">
                <div className="cf-field">
                  <div className="cf-field-label">Payment Gateway</div>
                  <div className="cf-field-val">
                    <span style={{ color: '#1d4ed8', fontWeight: 800 }}>{getGateway(selPayment)}</span>
                  </div>
                </div>

                <div className="cf-field">
                  <div className="cf-field-label">Payment Method</div>
                  <div className="cf-field-val">
                    <span>{selPayment.paymentMethod || selPayment.method || 'ONLINE'}</span>
                  </div>
                </div>

                <div className="cf-field">
                  <div className="cf-field-label">Cashfree Order ID</div>
                  <div className="cf-field-val">
                    <span className="cf-code cf-code-order">{getCfOrderId(selPayment)}</span>
                    {getCfOrderId(selPayment) !== EMPTY_TEXT && (
                      <button
                        type="button"
                        className="cf-copy-btn"
                        title="Copy Cashfree Order ID"
                        onClick={() => handleCopy(getCfOrderId(selPayment), 'cfOrder')}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                        </svg>
                      </button>
                    )}
                    {copiedKey === 'cfOrder' && <span className="cf-copied-toast">Copied!</span>}
                  </div>
                </div>

                <div className="cf-field">
                  <div className="cf-field-label">Cashfree Payment ID</div>
                  <div className="cf-field-val">
                    <span className="cf-code" style={{ color: '#0284c7' }}>{getCfPaymentId(selPayment)}</span>
                    {getCfPaymentId(selPayment) !== EMPTY_TEXT && (
                      <button
                        type="button"
                        className="cf-copy-btn"
                        title="Copy Cashfree Payment ID"
                        onClick={() => handleCopy(getCfPaymentId(selPayment), 'cfPay')}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                        </svg>
                      </button>
                    )}
                    {copiedKey === 'cfPay' && <span className="cf-copied-toast">Copied!</span>}
                  </div>
                </div>

                <div className="cf-field">
                  <div className="cf-field-label">Internal Payment ID</div>
                  <div className="cf-field-val">
                    <span className="cf-code">{selPayment._id || EMPTY_TEXT}</span>
                    {selPayment._id && (
                      <button
                        type="button"
                        className="cf-copy-btn"
                        title="Copy Payment ID"
                        onClick={() => handleCopy(selPayment._id, 'payId')}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                        </svg>
                      </button>
                    )}
                    {copiedKey === 'payId' && <span className="cf-copied-toast">Copied!</span>}
                  </div>
                </div>

                <div className="cf-field">
                  <div className="cf-field-label">Transaction Date &amp; Time</div>
                  <div className="cf-field-val">
                    <span>{fmtDateTime(selPayment.createdAt)}</span>
                  </div>
                </div>

                {selPayment.paymentSessionId && (
                  <div className="cf-field cf-grid-full">
                    <div className="cf-field-label">Payment Session ID</div>
                    <div className="cf-field-val">
                      <span className="cf-code" style={{ fontSize: 11, wordBreak: 'break-all', color: '#64748b' }}>
                        {selPayment.paymentSessionId}
                      </span>
                      <button
                        type="button"
                        className="cf-copy-btn"
                        title="Copy Session ID"
                        onClick={() => handleCopy(selPayment.paymentSessionId, 'sessId')}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                        </svg>
                      </button>
                      {copiedKey === 'sessId' && <span className="cf-copied-toast">Copied!</span>}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Customer Details Card */}
            <div className="cf-modal-card">
              <div className="cf-modal-card-title">Customer Information</div>
              <div className="cf-grid-2">
                <div className="cf-field">
                  <div className="cf-field-label">Customer Name</div>
                  <div className="cf-field-val">{getCustomerName(selPayment)}</div>
                </div>
                <div className="cf-field">
                  <div className="cf-field-label">Customer ID</div>
                  <div className="cf-field-val">
                    <span className="cf-code" style={{ fontSize: 11 }}>{getCustomerId(selPayment)}</span>
                  </div>
                </div>
                <div className="cf-field">
                  <div className="cf-field-label">Email Address</div>
                  <div className="cf-field-val">{getCustomerEmail(selPayment)}</div>
                </div>
                <div className="cf-field">
                  <div className="cf-field-label">Phone Number</div>
                  <div className="cf-field-val">{getCustomerPhone(selPayment)}</div>
                </div>
              </div>
            </div>

            {/* Order Details Card */}
            <div className="cf-modal-card">
              <div className="cf-modal-card-title">Associated Order Information</div>
              <div className="cf-grid-2">
                <div className="cf-field">
                  <div className="cf-field-label">Order ID</div>
                  <div className="cf-field-val">
                    <span className="cf-code cf-code-order">#{getOrderId(selPayment)}</span>
                    {getOrderId(selPayment) !== EMPTY_TEXT && (
                      <button
                        type="button"
                        className="cf-copy-btn"
                        title="Copy Order ID"
                        onClick={() => handleCopy(getOrderId(selPayment), 'orderId')}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                        </svg>
                      </button>
                    )}
                    {copiedKey === 'orderId' && <span className="cf-copied-toast">Copied!</span>}
                  </div>
                </div>

                <div className="cf-field">
                  <div className="cf-field-label">Order Status</div>
                  <div className="cf-field-val">
                    <span style={{ textTransform: 'capitalize', color: '#6D28D9', fontWeight: 700 }}>
                      {orderDetail?.orderStatus || orderDetail?.status || selPayment.order?.orderStatus || selPayment.order?.status || 'Pending'}
                    </span>
                  </div>
                </div>

                {(orderDetail?.createdAt || selPayment.order?.createdAt) && (
                  <div className="cf-field">
                    <div className="cf-field-label">Order Created Date</div>
                    <div className="cf-field-val">{fmtDateTime(orderDetail?.createdAt || selPayment.order?.createdAt)}</div>
                  </div>
                )}

                {(orderDetail?.totalAmount !== undefined || selPayment.order?.totalAmount !== undefined) && (
                  <div className="cf-field">
                    <div className="cf-field-label">Order Total Amount</div>
                    <div className="cf-field-val" style={{ color: '#059669' }}>
                      {fmtAmount(orderDetail?.totalAmount ?? selPayment.order?.totalAmount)}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Purchased Products Card */}
            {(() => {
              const effectiveOrder = orderDetail || (typeof selPayment.order === 'object' ? selPayment.order : null);
              const orderItems = effectiveOrder?.products || effectiveOrder?.items || selPayment.products || selPayment.items || [];
              const itemsSubtotal = orderItems.reduce((sum, it) => sum + (getItemPrice(it) * getItemQuantity(it)), 0);
              const discountAmount = Number(effectiveOrder?.discountAmount || effectiveOrder?.couponDiscount || effectiveOrder?.coupon?.discountAmount || 0);
              const couponCode = effectiveOrder?.couponCode || effectiveOrder?.coupon?.code || (typeof effectiveOrder?.coupon === 'string' ? effectiveOrder?.coupon : '');
              const superCoinsDiscount = Number(effectiveOrder?.superCoinsDiscount || effectiveOrder?.superCoinsAmount || effectiveOrder?.superCoins?.amount || 0);
              const shippingAmount = effectiveOrder?.shippingCharge !== undefined ? Number(effectiveOrder.shippingCharge) : (effectiveOrder?.shippingCharges !== undefined ? Number(effectiveOrder.shippingCharges) : undefined);
              const finalOrderAmount = effectiveOrder?.totalAmount ?? effectiveOrder?.finalPayableAmount ?? selPayment.order?.totalAmount ?? selPayment.amount;

              return (
                <div className="cf-modal-card">
                  <div className="cf-modal-card-title">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#2d5f6e" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
                        <line x1="3" y1="6" x2="21" y2="6"/>
                        <path d="M16 10a4 4 0 0 1-8 0"/>
                      </svg>
                      <span>Purchased Product Details</span>
                    </div>
                    {orderItems.length > 0 && (
                      <span style={{
                        background: '#eff6ff',
                        color: '#1d4ed8',
                        border: '1px solid #bfdbfe',
                        padding: '2px 9px',
                        borderRadius: 12,
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: 'none',
                        letterSpacing: 0
                      }}>
                        {orderItems.length} {orderItems.length === 1 ? 'item' : 'items'}
                      </span>
                    )}
                  </div>

                  {loadingOrder ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '24px 0', color: '#64748b', fontSize: 13 }}>
                      <div className="pay-spinner" style={{ width: 20, height: 20, borderWidth: 2 }} />
                      <span>Loading product items...</span>
                    </div>
                  ) : orderItems.length === 0 ? (
                    <div style={{
                      textAlign: 'center',
                      padding: '20px 14px',
                      color: '#64748b',
                      fontSize: 13,
                      background: '#ffffff',
                      borderRadius: 10,
                      border: '1px dashed #cbd5e1'
                    }}>
                      No product items recorded for this order.
                    </div>
                  ) : (
                    <div>
                      <div className="cf-product-list">
                        {orderItems.map((item, idx) => {
                          const name = getItemName(item, idx);
                          const price = getItemPrice(item);
                          const qty = getItemQuantity(item);
                          const variant = getItemVariant(item);
                          const sku = getItemSku(item);
                          const img = getItemImage(item);
                          const subtotal = price * qty;

                          return (
                            <div key={`order-prod-${idx}`} className="cf-product-item">
                              <ProductThumb src={img} name={name} size={54} />
                              <div className="cf-product-info">
                                <div className="cf-product-title-row">
                                  <span className="cf-product-name" title={name}>{name}</span>
                                </div>
                                <div className="cf-product-meta-row">
                                  {variant && (
                                    <span className="cf-product-badge">
                                      {variant}
                                    </span>
                                  )}
                                  {sku && (
                                    <span className="cf-product-sku">
                                      {sku}
                                    </span>
                                  )}
                                  <span className="cf-product-unit-price">
                                    {fmtAmount(price)} each
                                  </span>
                                </div>
                              </div>
                              <div className="cf-product-pricing">
                                <span className="cf-product-qty">Qty: {qty}</span>
                                <span className="cf-product-total">{fmtAmount(subtotal)}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Order Financial Breakdown */}
                      <div className="cf-order-summary-box">
                        <div className="cf-summary-row">
                          <span>Items Subtotal</span>
                          <span style={{ fontWeight: 600, color: '#102a33' }}>{fmtAmount(itemsSubtotal || finalOrderAmount)}</span>
                        </div>
                        {discountAmount > 0 && (
                          <div className="cf-summary-row" style={{ color: '#059669' }}>
                            <span>Discount {couponCode ? `(${couponCode})` : ''}</span>
                            <span style={{ fontWeight: 600 }}>-{fmtAmount(discountAmount)}</span>
                          </div>
                        )}
                        {superCoinsDiscount > 0 && (
                          <div className="cf-summary-row" style={{ color: '#059669' }}>
                            <span>SuperCoins Discount</span>
                            <span style={{ fontWeight: 600 }}>-{fmtAmount(superCoinsDiscount)}</span>
                          </div>
                        )}
                        {shippingAmount !== undefined && (
                          <div className="cf-summary-row">
                            <span>Shipping Charges</span>
                            <span style={{ fontWeight: 600, color: shippingAmount === 0 ? '#059669' : '#102a33' }}>
                              {shippingAmount === 0 ? 'FREE' : fmtAmount(shippingAmount)}
                            </span>
                          </div>
                        )}
                        <div className="cf-summary-row cf-summary-total">
                          <span>Total Order Amount</span>
                          <span style={{ color: '#059669', fontSize: 15, fontWeight: 800 }}>
                            {fmtAmount(finalOrderAmount)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Delivery & Shipping Address Card (if available) */}
            {(() => {
              const effectiveOrder = orderDetail || (typeof selPayment.order === 'object' ? selPayment.order : null);
              const shippingAddr = getShippingAddress(effectiveOrder);
              if (!shippingAddr) return null;

              return (
                <div className="cf-modal-card">
                  <div className="cf-modal-card-title">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#2d5f6e" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                      <span>Delivery &amp; Shipping Address</span>
                    </div>
                  </div>
                  <div className="cf-grid-2">
                    {shippingAddr.name && (
                      <div className="cf-field">
                        <div className="cf-field-label">Recipient Name</div>
                        <div className="cf-field-val">{shippingAddr.name}</div>
                      </div>
                    )}
                    {shippingAddr.phone && (
                      <div className="cf-field">
                        <div className="cf-field-label">Contact Phone</div>
                        <div className="cf-field-val">{shippingAddr.phone}</div>
                      </div>
                    )}
                    <div className="cf-field cf-grid-full">
                      <div className="cf-field-label">Delivery Address</div>
                      <div className="cf-field-val" style={{ fontWeight: 500, lineHeight: 1.5, color: '#334155' }}>
                        {shippingAddr.raw || [
                          shippingAddr.line1,
                          shippingAddr.line2,
                          shippingAddr.city,
                          shippingAddr.state,
                          shippingAddr.pincode
                        ].filter(Boolean).join(', ')}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Refund Information if present */}
            {(selPayment.refundedAmount > 0 || selPayment.refundId) && (
              <div className="cf-modal-card" style={{ borderColor: '#fecaca', background: '#fef2f2' }}>
                <div className="cf-modal-card-title" style={{ color: '#dc2626' }}>Refund Information</div>
                <div className="cf-grid-2">
                  <div className="cf-field">
                    <div className="cf-field-label">Refunded Amount</div>
                    <div className="cf-field-val" style={{ color: '#dc2626' }}>{fmtAmount(selPayment.refundedAmount)}</div>
                  </div>
                  <div className="cf-field">
                    <div className="cf-field-label">Refund ID</div>
                    <div className="cf-field-val cf-code">{selPayment.refundId || EMPTY_TEXT}</div>
                  </div>
                  {selPayment.refundReason && (
                    <div className="cf-field cf-grid-full">
                      <div className="cf-field-label">Refund Reason</div>
                      <div className="cf-field-val">{selPayment.refundReason}</div>
                    </div>
                  )}
                  {selPayment.refundedAt && (
                    <div className="cf-field">
                      <div className="cf-field-label">Refunded At</div>
                      <div className="cf-field-val">{fmtDateTime(selPayment.refundedAt)}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
              <button
                type="button"
                className="filter-export-btn"
                onClick={() => setSelPayment(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Payments;
